import { useState } from 'react';
import { ArrowLeft, ArrowRight, Upload } from 'lucide-react';
import { PALETA_BRANDING } from '@shared/constants';
import { TicketPreview } from '@renderer/components/TicketPreview';
import { aplicarColorPrimario } from '@renderer/utils/colors';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  onSiguiente: () => void;
  onAtras: () => void;
}

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export function Step2Branding({ onSiguiente, onAtras }: Props) {
  const [color, setColor] = useState<string>('#1976D2');
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nombrePreview, setNombrePreview] = useState('Mi Restaurante');

  const handleColorChange = (hex: string) => {
    setColor(hex);
    aplicarColorPrimario(hex);
  };

  const handleFile = async (file: File) => {
    if (file.size > MAX_LOGO_BYTES) {
      setError('Imagen muy grande. Usa una de máximo 2MB.');
      return;
    }
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Solo se aceptan archivos PNG o JPG.');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => setLogoBase64(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSiguiente = async () => {
    setEnviando(true);
    const res = await window.api.onboarding.guardarPaso(2, {
      colorPrimario: color,
      logoFileBase64: logoBase64 ?? undefined,
    });
    setEnviando(false);
    if (res.ok) onSiguiente();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  const handleSaltar = async () => {
    setEnviando(true);
    const res = await window.api.onboarding.guardarPaso(2, {
      colorPrimario: '#1976D2',
    });
    setEnviando(false);
    if (res.ok) onSiguiente();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Personaliza tu marca</h1>
        <p className="mt-2 text-slate-500">
          Tu logo aparecerá en la app y en los tickets impresos.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="card space-y-4">
          <div>
            <div className="label">Logo</div>
            <div className="flex items-center gap-4">
              <div className="flex h-24 w-24 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50">
                {logoBase64 ? (
                  <img src={logoBase64} alt="Logo" className="h-full w-full object-contain p-2" />
                ) : (
                  <span className="text-xs text-slate-400">Sin logo</span>
                )}
              </div>
              <label className="btn-secondary cursor-pointer">
                <Upload className="h-4 w-4" />
                <span>Subir imagen</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                />
              </label>
            </div>
            <p className="help-text">PNG o JPG, máximo 2MB</p>
          </div>

          <div>
            <div className="label">Color principal</div>
            <div className="flex flex-wrap gap-2">
              {PALETA_BRANDING.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => handleColorChange(c.hex)}
                  className={`h-10 w-10 rounded-full border-2 transition ${
                    color === c.hex ? 'border-slate-900 scale-110' : 'border-white shadow'
                  }`}
                  style={{ backgroundColor: c.hex }}
                  aria-label={c.label}
                  title={c.label}
                />
              ))}
              <input
                type="color"
                value={color}
                onChange={(e) => handleColorChange(e.target.value)}
                className="h-10 w-10 rounded-full border-2 border-white shadow cursor-pointer"
                aria-label="Color personalizado"
              />
            </div>
          </div>
        </div>

        <div>
          <div className="label text-center">Vista previa del ticket</div>
          <TicketPreview
            nombre={nombrePreview}
            logoSrc={logoBase64 ?? undefined}
          />
          <input
            type="text"
            value={nombrePreview}
            onChange={(e) => setNombrePreview(e.target.value)}
            className="input mt-3 text-sm"
            placeholder="Nombre para preview"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</div>
      )}

      <div className="flex justify-between">
        <button onClick={onAtras} className="btn-ghost btn-lg">
          <ArrowLeft className="h-5 w-5" />
          Atrás
        </button>
        <div className="flex gap-3">
          <button onClick={handleSaltar} className="btn-secondary btn-lg" disabled={enviando}>
            Saltar
          </button>
          <button onClick={handleSiguiente} className="btn-primary btn-lg" disabled={enviando}>
            Siguiente
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
